"use client";

import { TableHeadWithFontBold } from "@/components/Table";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import type { LatestLostInsuranceNumberIdsByMember } from "@/server/models/insurance-number/get-latest-lost-record";
import { getInsuranceCardDisplayName } from "@/server/models/insurance-number/insurance-card-name";
import type { Insurer } from "@/server/models/insurer/insurer";
import { formatDate } from "@/server/utils/format-date";
import {
  formatInsuranceNumberForDisplay,
  isOptionalInsuranceNumber,
} from "@/utils/format-insurance-number";
import { routePath } from "@/utils/pages-path";
import { formatRelationshipToDisplay } from "@/utils/relationships";
import Link from "next/link";
import { MdDownload } from "react-icons/md";
import { DownloadEligibilityInformationButton } from "./DownloadEligibilityInformationButton";
import type { InsuranceHistoryData } from "./types";

/**
 * ダウンロードセルの内容を表示するコンポーネント
 */
const DocumentCellContent = ({
  historyItem,
  latestLostRecordId,
  insurer,
}: {
  historyItem: InsuranceHistoryData;
  latestLostRecordId: string | undefined;
  insurer: Insurer;
}) => {
  // 資格喪失していない場合は資格情報のお知らせをダウンロード
  if (!historyItem.lostAt) {
    return (
      <DownloadEligibilityInformationButton
        className="w-full"
        insuranceNumber={historyItem}
        primaryMember={historyItem.primaryMember}
        insurer={insurer}
      >
        <div className="flex items-center gap-2">
          <MdDownload className="h-4 w-4" />
          <span>資格情報のお知らせ</span>
        </div>
      </DownloadEligibilityInformationButton>
    );
  }

  // 資格喪失証明書は各加入者の最新の資格喪失1件のみダウンロード可能（任意継続は除く）
  const canDownloadLossCertificate =
    historyItem.id === latestLostRecordId &&
    !isOptionalInsuranceNumber(historyItem.number);

  if (!canDownloadLossCertificate) {
    return <>-</>;
  }

  return (
    <Button asChild variant="secondary" className="w-full">
      <Link
        target="_blank"
        href={
          routePath.members.insurance_card.eligibility_loss_certificate
            ._insuranceNumberId(historyItem.id)
            .$url().path
        }
      >
        <div className="flex items-center gap-2">
          <MdDownload className="h-4 w-4" />
          <span>資格喪失証明書</span>
        </div>
      </Link>
    </Button>
  );
};

export const HistoryTable = ({
  history,
  latestLostRecordIdsByMember,
  isPrimary,
  onRowClick,
  insurer,
}: {
  history: InsuranceHistoryData[];
  latestLostRecordIdsByMember: LatestLostInsuranceNumberIdsByMember;
  isPrimary: boolean;
  onRowClick?: (data: InsuranceHistoryData) => void;
  insurer: Insurer;
}) => {
  return (
    <Table>
      <TableHeader className="h-14 bg-monotone-1 whitespace-nowrap md:whitespace-normal">
        <TableRow>
          <TableHeadWithFontBold className="w-[15%] text-sm">
            氏名
          </TableHeadWithFontBold>
          <TableHeadWithFontBold className="w-[15%] text-sm">
            記号-番号-枝番
          </TableHeadWithFontBold>
          {isPrimary ? (
            <TableHeadWithFontBold className="w-1/5 text-sm">
              事業所
            </TableHeadWithFontBold>
          ) : (
            <TableHeadWithFontBold className="w-1/5 text-sm">
              続柄
            </TableHeadWithFontBold>
          )}
          <TableHeadWithFontBold className="w-[15%] text-sm">
            資格取得日
          </TableHeadWithFontBold>
          <TableHeadWithFontBold className="w-[15%] text-sm">
            資格喪失日
          </TableHeadWithFontBold>
          <TableHeadWithFontBold className="w-1/5 text-sm">
            ダウンロード
          </TableHeadWithFontBold>
        </TableRow>
      </TableHeader>
      <TableBody>
        {history.map((historyItem) => {
          const number = formatInsuranceNumberForDisplay(historyItem.number);
          // 各加入者の最新の資格喪失レコードIDを取得
          const latestLostRecordId =
            latestLostRecordIdsByMember[historyItem.memberId]
              ?.insuranceNumberId;
          return (
            <TableRow
              key={historyItem.id}
              className="cursor-pointer transition-colors hover:bg-monotone-1"
              onClick={() => onRowClick?.(historyItem)}
            >
              <TableCell className="w-[15%] px-6 py-4 text-sm whitespace-nowrap">
                {getInsuranceCardDisplayName({
                  insuranceCardName: historyItem.insuranceCardName,
                  name: historyItem.name,
                })}
              </TableCell>
              <TableCell className="w-[15%] px-6 py-4 text-sm">
                {number}
              </TableCell>
              {isPrimary ? (
                <TableCell className="min-w-48 px-6 py-4 text-sm md:w-1/5 md:max-w-none">
                  {historyItem.office?.name}
                </TableCell>
              ) : (
                <TableCell className="w-1/5 px-6 py-4 text-sm">
                  {formatRelationshipToDisplay(historyItem.relationship)}
                </TableCell>
              )}
              <TableCell className="w-[15%] px-6 py-4 text-sm">
                {formatDate(historyItem.acquiredAt, DEFAULT_DATE_FORMAT)}
              </TableCell>
              <TableCell className="w-[15%] px-6 py-4 text-sm">
                {historyItem.lostAt
                  ? formatDate(historyItem.lostAt, DEFAULT_DATE_FORMAT)
                  : "-"}
              </TableCell>
              <TableCell className="w-1/5 px-6 py-4 text-sm">
                <div onClick={(e) => e.stopPropagation()}>
                  <DocumentCellContent
                    historyItem={historyItem}
                    latestLostRecordId={latestLostRecordId}
                    insurer={insurer}
                  />
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
